import React, { useState, useContext, Fragment, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {AppContext, CALENDAR_VIEW, GRID_VIEW} from "@/App";
import ListingSearch from "../components/listing/ListingSearch";
import CalendarIcon from "../components/utils/icons/CalendarIcon";
import GridViewIcon from "../components/utils/icons/GridViewIcon";
import ListingFooter from "../components/listing/ListingFooter";
import ListingList, { ListingListSkeleton } from "../components/listing/ListingList";
import { listingService } from "../services/ListingService";
import { debounce } from "@showclix-shared-util/debounce";
import { isAbortException } from "@showclix-shared-util/common";
// import Expander from "../components/utils/Expander";
// import Loader from "../components/utils/Loader";
import ListingCalendar from "../components/listing/ListingCalendar";
// import Button from "@showclix-shared/Button"
import Toast from "@showclix-shared/Toast"
import { BackgroundImage, UserPhoto, ListingHeading } from "@showclix-shared/ListingComponents";
import MembershipLoginView from "@/views/MembershipLoginView";
import { useAnalytics, useBaseEventData } from '@/contexts/Analytics';
import { useAnnouncer } from '@/hooks/useAnnouncer';
import { pluralSuffix } from '@/utils/pluralSuffix';

// if there's a requested listing document, force it to the top
const sortListings = (listingsToSort, requestedListingObject) => {
    const sorted = Array.isArray(listingsToSort) ? [...listingsToSort] : [];
    if (requestedListingObject?.listing_id) {
        sorted.sort((a,b) => {
            if (a.listing_id == requestedListingObject.listing_id) {
                return -1;
            }
            if (b.listing_id == requestedListingObject.listing_id) {
                return 1;
            }
            return 0;
        });
    }
    return sorted;
}

// Should be the same number in App/Services/OrganizationListingService.php
const LIMIT_PER_PAGE = 100;

const ListingView = (props) => {
    const announce = useAnnouncer('polite');

    const [filter, setFilter] = useState(undefined);

    const [listings, setFormattedListings] = useState(sortListings(props.listings, props.requestedListingDocument));
    const [listingsLoading, setListingsLoading] = useState(false);
    const abortControllerRef = useRef(null);
    const debounceTimerRef = useRef(null);

    const { membershipConfig, updateOrgListingConfig, tab, setTab } = useContext(AppContext);
    const [isMembershipLoggedIn, setMembershipLoggedIn] = useState(false);

    const { reportEvent, reportGTM } = useAnalytics();

    const baseEventData = useBaseEventData();

    const [offset, setOffset] = useState(0);

  /**
     * Set the listings keeping in mind that if a requested listing
     * document is passed, it needs to be shown at the top so long as the
     * it's not filtered out of the results by the search bar
     */
    const setListings = providedListings => {
        let toSet = Array.isArray(providedListings) ? [...providedListings] : [];

        // if the user is not searching, and there's a requested
        // listing, force it into the result set
        if (
            Array.isArray(providedListings) &&
            props.requestedListingDocument &&
            !providedListings.find(doc => doc.listing_id == props.requestedListingDocument?.listing_id) &&
            !filter
        ) {
            toSet.push(props.requestedListingDocument);
        }

        setFormattedListings(sortListings(toSet, props.requestedListingDocument));
    };

    // Shouldn’t be shown in Phase 1
    // const [pastListings, setPastListings] = useState([]);
    // const [canLoadMore, setCanLoadMore] = useState(false);
    // const [pastListingLimit, setPastListingLimit] = useState(PAST_LISTING_LIMIT)
    // const [pastListingLoaded, setPastListingLoaded] = useState(false);

    const { apiUrl, sellerShortName, t, hideSearchAndDisplayControls } = useContext(AppContext);

    // Shouldn’t be shown in Phase 1
    // getPastListings
    const { getUpcomingListings } = useMemo(
        () => listingService(apiUrl, t),
        [apiUrl, t]
    );

    const onClickIcon = (icon) => {
      const url = new URL(window.location.href);

      if (icon === GRID_VIEW) {
        // Strip month from URL when switching to grid and persist selected view.
        url.searchParams.delete('month');
        const nextUrl = `${url.pathname}${url.search}${url.hash}`;
        window.history.pushState({ ...(window.history.state || {}), view: GRID_VIEW }, '', nextUrl);
      } else {
        // Preserve existing query params (including month) and persist selected view.
        const nextUrl = `${url.pathname}${url.search}${url.hash}`;
        window.history.pushState({ ...(window.history.state || {}), view: CALENDAR_VIEW }, '', nextUrl);
      }

        setTab(icon);
        const nextView = icon === CALENDAR_VIEW ? 'calendar' : 'grid';
        updateOrgListingConfig({ view: nextView });
        reportEvent('google-analytics-4')('event', 'orglisting_display_change', {
            ...baseEventData,
            display_type: nextView
        });
        reportGTM({
            event: 'orglisting_display_change',
            ...baseEventData,
            display_type: nextView
        });
    }

    const isIconSelected = (icon) => tab === icon;

    const onFocusFilter = () => {
        reportEvent('google-analytics-4')('event', 'orglisting_search_bar', baseEventData);
    }

    const performSearch = async (searchValue, signal) => {
        setListingsLoading(true);
        try {
            const listingsResponse = await getUpcomingListings(
                sellerShortName,
                { name: searchValue },
                signal
            );
            
            if (!signal.aborted) {
                setListings(listingsResponse?.listings);
                const eventCount = listingsResponse?.listings?.length || 0;
                announce(t('org_listing__events_found_announce', { eventCount, pluralSuffix: pluralSuffix(eventCount) }));
            }
        } catch (err) {
            if (!isAbortException(err)) {
                Toast.error(t('org_listing__search_error'));
            }
        } finally {
            setOffset(0);
            if (!signal.aborted) {
                setListingsLoading(false);
            }
        }
    };

    const debouncedSearch = useMemo(
        () => debounce(performSearch, 300),
        [sellerShortName, t]
    );

    const onChangeFilter = (e) => {
        tab !== GRID_VIEW && onClickIcon(GRID_VIEW);
        const searchValue = e.target.value;
        setFilter(searchValue);

        abortControllerRef.current?.abort();

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (!searchValue || searchValue.trim() === '') {
            setListingsLoading(false);
            setListings(props.listings);
            setOffset(0);
            return;
        }

        if (searchValue.trim().length < 3) {
            return;
        }

        abortControllerRef.current = new AbortController();
        
        debounceTimerRef.current = debouncedSearch(
            searchValue,
            abortControllerRef.current.signal
        );
    }

    // TODO: Implement in TT-7484
    const getMoreEvents = async () => {
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      setListingsLoading(true);
      const prevOffset = offset;
      try {
        const newOffset = prevOffset + LIMIT_PER_PAGE;
        setOffset(newOffset);
        const listingsResponse = await getUpcomingListings(sellerShortName, { name: filter ?? null, offset: newOffset }, signal);

        if (!signal.aborted && listingsResponse && Array.isArray(listingsResponse.listings)) {
          setFormattedListings(prev => [...prev, ...listingsResponse.listings]);
        }
      } catch (err) {
        if (!isAbortException(err)) {
          Toast.error(t('org_listing__search_error'));
        }
        if (!signal.aborted) {
          setOffset(prevOffset);
        }
      } finally {
        if (!signal.aborted) {
          setListingsLoading(false);
        }
      }
    };

    // Shouldn’t be shown in Phase 1
    // const onClosePastListings = async () => {
    //     setPastListingLimit(PAST_LISTING_LIMIT);
    //     setPastListings([]);
    // }

    // const onOpenPastListings = async () => {
    //     setPastListingLimit(PAST_LISTING_LIMIT);

    //     await resetPastListings(pastListingLimit);
    // }

    // const onClickLoadMore = async () => {
    //     if (pastListingLoaded) {
    //         const limit = pastListingLimit + PAST_LISTING_LIMIT;
    //         setPastListingLimit(limit);

    //         await resetPastListings(limit);
    //     }
    // }

    // const resetPastListings = async (limit) => {
    //     setPastListingLoaded(false);
    //     try {
    //         const { pastListings, hasMore } = await getPastListings(sellerShortName, limit);

    //         setPastListings(pastListings);
    //         setCanLoadMore(hasMore);
    //     } catch (e) {
    //         Toast.error("An error occurred when loading past events. Please, try again.");
    //         setPastListings([]);
    //         setCanLoadMore(false);
    //     } finally {
    //         setPastListingLoaded(true);
    //     }
    // }

    // Cleanup: abort any pending requests and clear timers when component unmounts
    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    return (
      <>
        <BackgroundImage image={props.backgroundImage} />
        <main className="org-listing__main">
          <div
            className={`org-listing__main__container org-listing__container ${
              !props.userPhoto ? 'org-listing__main__container--padding-top org-listing__container--no-left' : ''
            }`}
          >
            {props.userPhoto && (
              <UserPhoto className="listing__user-photo" image={props.userPhoto} name={props.organizationInfo.name} />
            )}
            <ListingHeading
              primaryText={props.organizationInfo.name}
              secondaryText={props.description}
              className={props.userPhoto ? 'org-listing__user-text--margin-left' : ''}
            />
            {membershipConfig && (
              <div className={`org-listing__upcoming-title ${!props.description && props.userPhoto ? 'org-listing__upcoming-title--extra-margin' : ''}`}
                   style={{ display: isMembershipLoggedIn ? 'none' : undefined }}>
                  <MembershipLoginView onMembershipLoginChange={setMembershipLoggedIn} />
              </div>
            )}

            {!(membershipConfig && !isMembershipLoggedIn) && (
              <>
                {!hideSearchAndDisplayControls && (
                  <div
                    className={`org-listing__upcoming-title ${
                      !props.description && props.userPhoto ? 'org-listing__upcoming-title--extra-margin' : ''
                    }`}
                  >
                    <span className="text-xl-medium">{t('org_listing__upcoming_events')}</span>
                    <div className="org-listing__upcoming-title-btns-container">
                      <GridViewIcon selected={isIconSelected(GRID_VIEW)} onClick={() => onClickIcon(GRID_VIEW)} />
                      <CalendarIcon selected={isIconSelected(CALENDAR_VIEW)} onClick={() => onClickIcon(CALENDAR_VIEW)} />
                    </div>
                  </div>
                )}
                {!hideSearchAndDisplayControls && (
                  <div className="listing-items__tools">
                    <ListingSearch className="listing-search" onChange={onChangeFilter} onFocus={onFocusFilter}/>
                    <GridViewIcon selected={isIconSelected(GRID_VIEW)} onClick={() => onClickIcon(GRID_VIEW)}/>
                    <CalendarIcon selected={isIconSelected(CALENDAR_VIEW)} onClick={() => onClickIcon(CALENDAR_VIEW)}/>
                  </div>
                )}
                {isIconSelected(GRID_VIEW) ? (
                  <Fragment>
                    {listingsLoading ? (
                      <ListingListSkeleton/>
                    ) : (
                      <ListingList
                        listings={listings ?? []}
                        filter={filter ?? ''}
                        noItemsText={t('org_listing__no_upcoming_events')}
                      />
                    )}
                    {/* Shouldn’t be shown in Phase 1 */}
                    {/* <Expander
                                className='mt-4 mb-4'
                                title={t('org_listing__past_events')}
                                onOpen={onOpenPastListings}
                                onClose={onClosePastListings}
                            >
                                {pastListingLoaded
                                    ? <>
                                        <ListingList noItemsText={t('org_listing__no_past_events_were_found')} listings={pastListings}/>
                                        {canLoadMore &&
                                            <div className='listing__load-more'>
                                                <Button variant='default' onClick={onClickLoadMore}>{t("org_listing__load_more")}</Button>
                                            </div>
                                        }
                                    </>
                                    : <ListingListSkeleton/>
                                }
                            </Expander> */}
                  </Fragment>
                ) : (
                  <ListingCalendar/>
                )}
              </>
            )}
            {props?.organizationInfo && (
              <ListingFooter
                sellerId={props.sellerId}
                organizationInfo={props.organizationInfo}
                socialMedia={props.socialMedia}
                isPro={props.isPro}
              />
            )}
          </div>
        </main>
      </>
    );
}

ListingView.propTypes = {
    listings: PropTypes.array.isRequired,
    userPhoto: PropTypes.string,
    backgroundImage: PropTypes.string,
    description: PropTypes.string,
    organizationInfo: PropTypes.object,
    socialMedia: PropTypes.object,
}

export default ListingView;
